import {getTestProps} from '@benchling/js-test-utils';
import {Button} from '@mantine/core';
import React, {useCallback} from 'react';

import ArchiveModal from 'archive/components/modals/ArchiveModal';
import {getOrCreateDraftState, setDraftState, ChatDraftStatus} from 'llm/chat-library/ChatDraftState';
import ChatSettingsListingView from 'llm/chat-library/shared/list-detail/ChatSettingsListingView';
import type {ListGroup} from 'llm/chat-library/shared/list-detail/listDetailTypes';
import {
  SkillsGroup,
  useAISidepanelGlobalClientState,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {buildSkillDraftText, SkillChatMode} from 'llm/chat-library/skills/SkillActionsMenu';
import SkillDetailPanel, {SkillDetailTab} from 'llm/chat-library/skills/SkillDetailPanel';
import type {SkillSummary} from 'llm/chat-library/skills/SkillsDAO';
import {toSkillListItem} from 'llm/chat-library/skills/skillsModalAdapter';
import UploadSkillModal from 'llm/chat-library/skills/UploadSkillModal';
import useSkills from 'llm/chat-library/skills/useSkills';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';

/**
 * The Skills tab's groups, in display order. `id` is the group's URL `?group=` key and the tab
 * value; `label` is the tab text; `filter` selects the group's skills. The listing view derives the
 * tab bar from these.
 */
const SKILLS_GROUPS: Array<{
  id: SkillsGroup;
  label: string;
  filter: (skill: SkillSummary) => boolean;
}> = [
  {id: SkillsGroup.ALL, label: 'All', filter: () => true},
  {id: SkillsGroup.MINE, label: 'My skills', filter: (s) => s.isOwnedByCurrentUser},
  {id: SkillsGroup.SHARED, label: 'Shared with me', filter: (s) => !s.isOwnedByCurrentUser},
];

/**
 * Displays the Skills tab in the chat settings, with group tabs and a skill detail panel.
 * Uses the URL to determine selected skill and tab, and handles navigation via callbacks.
 */
export default function ChatSettingsSkillContent({
  selectedItemId,
  group,
  onSelectItem,
  onGroupChange,
  onBack,
  onClose,
}: {
  selectedItemId: string | null;
  group: SkillsGroup;
  onSelectItem: (id: string) => void;
  onGroupChange: (group: SkillsGroup) => void;
  onBack: () => void;
  /** Closes the whole settings modal (e.g. after bringing a skill into chat). */
  onClose: () => void;
}): JSX.Element {
  const {skills, isLoading, enableSkill, disableSkill, archiveSkill, createNewSkills, refresh} = useSkills();
  const [panelState] = useAISidepanelGlobalClientState();

  const handleToggle = useCallback(
    async (skill: SkillSummary, enabled: boolean) => {
      if (enabled) {
        await enableSkill(skill.id);
      } else {
        await disableSkill(skill.id);
      }
    },
    [enableSkill, disableSkill]
  );

  // Bring a skill into the active chat by prefilling the draft input, then close the settings modal
  // so the chat is visible. Mirrors SkillActionsMenu's "Try/Edit with chat" behavior.
  const handleTryWithChat = useCallback(
    (skill: SkillSummary, mode: SkillChatMode) => {
      const chatSessionId = panelState.activeChat.id;
      const draftState = getOrCreateDraftState(chatSessionId);
      setDraftState(chatSessionId, {
        ...draftState,
        status: ChatDraftStatus.DRAFT,
        message: {
          ...draftState.message,
          text: buildSkillDraftText(draftState.message.text, skill, mode),
        },
      });
      onClose();
    },
    [panelState, onClose]
  );

  // TODO (alden): This should open the skill's Manage access tab. A larger refactor will come in for
  // the detail page which will include this change.
  const handleManageAccess = useCallback(
    (skill: SkillSummary) => {
      onSelectItem(skill.id);
    },
    [onSelectItem]
  );

  const handleArchive = useCallback(
    (skill: SkillSummary) => {
      legacyRunModal(ArchiveModal, {
        items: [{id: skill.id, name: skill.name}],
        onArchive: (itemIds: Array<string>, purpose: string) => {
          for (const id of itemIds) {
            archiveSkill(id, purpose).catch(() => undefined);
          }
        },
      });
    },
    [archiveSkill]
  );

  const selectedSkill = selectedItemId != null ? (skills.find((s) => s.id === selectedItemId) ?? null) : null;

  if (selectedSkill != null) {
    return (
      <SkillDetailPanel
        skill={selectedSkill}
        onClose={onBack}
        onUpdate={refresh}
        onArchive={archiveSkill}
        initialTab={SkillDetailTab.DETAIL}
      />
    );
  }

  const handlers = {
    onToggleEnabled: handleToggle,
    onTryWithChat: handleTryWithChat,
    onManageAccess: handleManageAccess,
    onArchive: handleArchive,
  };

  const groups: Array<ListGroup> = SKILLS_GROUPS.map(({id, label, filter}) => ({
    id,
    header: label,
    items: skills.filter((s) => filter(s)).map((s) => toSkillListItem(s, handlers)),
  }));

  const addButton = (
    <Button
      variant="primary"
      size="xs"
      leftIcon={<BenchlingIcon icon="plus" />}
      onClick={() => {
        legacyRunModal(UploadSkillModal, {onRequestSubmit: createNewSkills});
      }}
      {...getTestProps({component: 'ChatSettingsSkillContent', element: 'uploadSkillButton'})}
    >
      Add
    </Button>
  );

  return (
    <ChatSettingsListingView
      header={{
        title: 'Skills',
        action: addButton,
      }}
      groups={groups}
      activeGroupId={group}
      onGroupChange={(value) => {
        onGroupChange(value as SkillsGroup);
      }}
      isLoading={isLoading}
      emptyMessage="No skills yet."
      selectedItemId={selectedItemId}
      onSelectItem={onSelectItem}
    />
  );
}
